import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { IconArrowLeft, IconCheck } from "@tabler/icons-react";
import { Button, Input, Label } from "@/components/ui";
import { publicApi } from "@/services/api";

interface AccessRequestForm {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string;
  email: string;
  telefone: string;
  contatoNome: string;
}

// Sócios e Parceiros data (mesmo do login)
const socios = [
  {
    name: "Patria Investimentos",
    logo: "/images/Logo Patria Investimentos.png",
  },
  {
    name: "Galapagos Capital",
    logo: "/images/Logo_Galapagos_Capital_Color (1).png",
  },
  {
    name: "Itaú",
    logo: "/images/Logo Itau.png",
  },
  {
    name: "Honey Island",
    logo: "/images/Logo Honey Island.png",
  },
  {
    name: "Oliveira Trust",
    logo: "/images/Logo OLIVEIRA TRUST.png",
  },
];

// Máscaras
function formatCNPJ(value: string): string {
  const numbers = value.replace(/\D/g, "").slice(0, 14);
  return numbers
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}

function formatPhone(value: string): string {
  const numbers = value.replace(/\D/g, "").slice(0, 11);
  if (numbers.length <= 10) {
    return numbers
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  }
  return numbers
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2");
}

export function AccessRequestPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<AccessRequestForm>();

  const onSubmit = async (data: AccessRequestForm) => {
    setIsLoading(true);
    setError(null);

    try {
      await publicApi.accessRequest(data);
      setIsSuccess(true);
    } catch (err: any) {
      setError(err.message || "Erro ao enviar solicitação");
    } finally {
      setIsLoading(false);
    }
  };

  // Tela de sucesso
  if (isSuccess) {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row">
        {/* Left Side - Success Message */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center px-4 sm:px-8 md:px-16 lg:px-24 py-8 sm:py-12 lg:py-0 bg-white min-h-screen lg:min-h-0">
          <div className="w-full max-w-md mx-auto text-center">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <IconCheck className="w-7 h-7 sm:w-8 sm:h-8 text-green-600" strokeWidth={2} />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-900 mb-3 sm:mb-4">
              Solicitação enviada!
            </h1>
            <p className="text-sm sm:text-base text-neutral-500 mb-6 sm:mb-8">
              Recebemos sua solicitação de acesso. Nossa equipe irá analisar seus dados e entrará em contato em breve pelo email informado.
            </p>
            <Button
              onClick={() => navigate("/login")}
              className="w-full h-10 sm:h-11 text-white hover:opacity-95"
              style={{ backgroundColor: '#0038B5' }}
            >
              Voltar para o login
            </Button>
          </div>
        </div>

        {/* Right Side - Marketing Panel */}
        <MarketingPanel />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-4 sm:px-8 md:px-16 lg:px-24 py-6 sm:py-8 lg:py-0 bg-white">
        <div className="w-full max-w-md mx-auto">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <a
              href="/login"
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-[#0038B5] transition-colors mb-4 sm:mb-6"
            >
              <IconArrowLeft className="w-4 h-4" strokeWidth={2} />
              voltar para o login
            </a>
            <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-900 mb-2">
              Solicitar acesso
            </h1>
            <p className="text-sm sm:text-base text-neutral-500">
              Preencha os dados da sua empresa para solicitar acesso à plataforma
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="cnpj" className="text-sm font-medium text-neutral-700">
                CNPJ
              </Label>
              <Input
                id="cnpj"
                type="text"
                placeholder="00.000.000/0000-00"
                {...register("cnpj", {
                  required: "CNPJ é obrigatório",
                  pattern: {
                    value: /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/,
                    message: "CNPJ inválido",
                  },
                })}
                onChange={(e) => {
                  const formatted = formatCNPJ(e.target.value);
                  setValue("cnpj", formatted, { shouldValidate: true });
                }}
                error={errors.cnpj?.message}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="razaoSocial" className="text-sm font-medium text-neutral-700">
                Razão Social
              </Label>
              <Input
                id="razaoSocial"
                type="text"
                placeholder="Nome da empresa conforme CNPJ"
                {...register("razaoSocial", {
                  required: "Razão social é obrigatória",
                  minLength: {
                    value: 3,
                    message: "Razão social deve ter no mínimo 3 caracteres",
                  },
                })}
                error={errors.razaoSocial?.message}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="nomeFantasia" className="text-sm font-medium text-neutral-700">
                Nome Fantasia
              </Label>
              <Input
                id="nomeFantasia"
                type="text"
                placeholder="Nome comercial da empresa"
                {...register("nomeFantasia", {
                  required: "Nome fantasia é obrigatório",
                })}
                error={errors.nomeFantasia?.message}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium text-neutral-700">
                Email corporativo
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="contato@empresa.com"
                {...register("email", {
                  required: "Email é obrigatório",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Email inválido",
                  },
                })}
                error={errors.email?.message}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="telefone" className="text-sm font-medium text-neutral-700">
                Telefone
              </Label>
              <Input
                id="telefone"
                type="text"
                placeholder="(00) 00000-0000"
                {...register("telefone", {
                  required: "Telefone é obrigatório",
                  minLength: {
                    value: 14,
                    message: "Telefone inválido",
                  },
                })}
                onChange={(e) => {
                  const formatted = formatPhone(e.target.value);
                  setValue("telefone", formatted, { shouldValidate: true });
                }}
                error={errors.telefone?.message}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="contatoNome" className="text-sm font-medium text-neutral-700">
                Nome do responsável
              </Label>
              <Input
                id="contatoNome"
                type="text"
                placeholder="Nome completo"
                {...register("contatoNome", {
                  required: "Nome do responsável é obrigatório",
                  minLength: {
                    value: 3,
                    message: "Nome deve ter no mínimo 3 caracteres",
                  },
                })}
                error={errors.contatoNome?.message}
              />
            </div>

            <Button
              type="submit"
              className="w-full h-10 sm:h-11 text-white hover:opacity-95 focus-visible:ring-[#0038B5] mt-4 sm:mt-6"
              style={{ backgroundColor: '#0038B5' }}
              loading={isLoading}
            >
              Enviar solicitação
            </Button>
          </form>

          {/* Footer */}
          <p className="mt-6 sm:mt-8 text-center text-sm text-neutral-400">
            Já tem uma conta?{" "}
            <a href="/login" className="text-neutral-900 font-medium hover:underline">
              Faça login
            </a>
          </p>
        </div>
      </div>

      {/* Right Side - Marketing Panel */}
      <MarketingPanel />
    </div>
  );
}

// Componente do painel de marketing (reutilizado)
function MarketingPanel() {
  return (
    <div
      className="hidden lg:flex lg:w-1/2 flex-col justify-center p-8 lg:p-12 relative overflow-hidden"
      style={{ background: '#0038B5' }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-2xl mx-auto w-full flex flex-col justify-between min-h-[60vh]">
        {/* Top Section - Logo and Headline */}
        <div className="text-center">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <img
              src="/images/logo-brld.svg"
              alt="BRLD Logo"
              className="h-8 lg:h-10 w-auto brightness-0 invert"
            />
            <span className="text-lg lg:text-xl font-semibold text-white">stableBRLD</span>
          </div>

          {/* Headline */}
          <div className="mb-12">
            <h2 className="text-2xl lg:text-3xl xl:text-4xl font-semibold text-white leading-tight mb-4">
              O real programável<br />
              para sua tesouraria
            </h2>
            <p className="text-sm lg:text-base text-white/80 max-w-md mx-auto">
              Stablecoin institucional pareada 1:1 ao real para liquidação e modernização da tesouraria corporativa.
            </p>
          </div>
        </div>

        {/* Bottom Section - Sócios e Parceiros */}
        <div className="mt-auto">
          <p className="text-xs text-white/70 text-center mb-6 lg:mb-8 font-medium uppercase tracking-wider">
            Sócios e Parceiros
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 lg:gap-8">
            {socios.map((socio, index) => {
              const isSmallerLogo = socio.name === "Patria Investimentos";
              const isItau = socio.name === "Itaú";
              const isHoneyIsland = socio.name === "Honey Island";
              return (
                <div
                  key={index}
                  className={`flex items-center justify-center opacity-80 hover:opacity-100 transition-all duration-300 ${
                    isItau || isHoneyIsland ? "h-10 lg:h-12" : isSmallerLogo ? "h-8 lg:h-10" : "h-6 lg:h-8"
                  }`}
                >
                  <img
                    src={socio.logo}
                    alt={`Logo ${socio.name}`}
                    className={`w-auto object-contain brightness-0 invert ${
                      isItau || isHoneyIsland ? "max-h-10 lg:max-h-12" : isSmallerLogo ? "max-h-8 lg:max-h-10" : "max-h-5 lg:max-h-6"
                    }`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
